import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { BUTTONS, EDIT_CUSTOMER, QUERY_KEYS, SYMBOL } from '../../../constants';
import { popup, updateHiringClientOrder } from '../../../utils';
import { Heading } from './Heading';
import { SectionBox } from './SectionBox';
import { ConfirmationBox } from '../../shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleSpinner } from '../../../assets/svg';

export const CSMHiringClientOrder = () => {
    const location = useLocation();
    const queryClient = useQueryClient();
    const accountData = location?.state?.accountData || [];
    const [tpa, setTpa] = useState(() => location?.state?.tpa || accountData?.platformsWithHC?.[0]);
    const [data, setData] = useState([]);
    const [hiringClients, setHiringClients] = useState([]);
    const [initialHiringClients, setInitialHiringClients] = useState([]);
    const [orderingList, setOrderingList] = useState({});
    const [openConfirmationBox, setOpenConfirmationBox] = useState(false);

    useEffect(() => {
        if (accountData && accountData?.platformsWithHC) {
            setData(accountData?.platformsWithHC);
            setInitialHiringClients(transformData(accountData?.platformsWithHC));
            setOrderingList({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData, tpa]);

    useEffect(() => {
        if (tpa && initialHiringClients[tpa.id]) {
            setHiringClients(initialHiringClients[tpa.id]);
        }
    }, [initialHiringClients, tpa]);

    function transformData(data) {
        const result = {};
        initialHiringClients;
        data.forEach((item) => {
            if (item.hiringClients) {
                result[item.id] = item.hiringClients;
            } else {
                result[item.id] = [];
            }
        });
        return result;
    }

    const onDragEnd = async (e) => {
        if (!e.destination) return;
        if (e.source.index === e.destination.index) return;
        const sorted = reorder(hiringClients, e.source.index, e.destination.index);
        setHiringClients(sorted);
        const updatedHiringClients = {
            ...initialHiringClients,
            [tpa.id]: sorted,
        };
        setInitialHiringClients(updatedHiringClients);
        const data = sorted.map(({ achcp_id, hc_order }) => ({ achcp_id, hc_order }));
        const ordered = {
            ...orderingList,
            [tpa.id]: data,
        };
        setOrderingList(ordered);
    };
    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const reorderHcMutation = useMutation({
        mutationFn: async () => await updateHiringClientOrder({ data: orderingList }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ACCOUNT_BY_ID, accountData.account.account_id],
            });
            setOrderingList({});
            popup('success', `Hiring Clients order updated for ${tpa?.name}!`);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const onConfirmHandler = (v) => {
        if (v) reorderHcMutation.mutate();
    };

    return (
        <div className='edit-box-customer'>
            <Heading data={accountData || {}} heading={EDIT_CUSTOMER.HEADING} />
            <div className='tabs-set'>
                <div className='tabs-row d-flex align-items-center justify-content-start'>
                    <span
                        className={`d-flex align-items-center justify-content-center link-profile active`}
                    >
                        {'Reorder Hiring Clients'}
                    </span>
                </div>
            </div>
            <SectionBox>
                <div className='d-flex flex-column justify-content-between align-item-center ps-lg-2 pe-lg-2 '>
                    <div className='d-flex justify-content-start  align-item-center gap-4 overflow-y-auto '>
                        {data?.length ? (
                            <div className='d-flex w-100 justify-content-between align-item-center'>
                                <div className='d-flex w-50 align-item-center gap-2 justify-content-start'>
                                    {data.map((platform, index) => (
                                        <span
                                            key={`${platform.id}-${index}`}
                                            className={`cursor-pointer ${platform.id === tpa.id ? 'ssc-primary-green-btn' : 'ssc-secondary-white-btn'} button-green-filled`}
                                            onClick={() => setTpa(platform)}
                                        >
                                            {platform?.name ?? ''}
                                            <sup>{SYMBOL.REGISTERED}</sup>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <span className='d-flex align-items-center ms-1 '>
                                {EDIT_CUSTOMER.BUTTON_TEXT.NO_PLATFORM}
                            </span>
                        )}
                    </div>
                </div>
                <div className='new-table-responsiveness ' key={tpa?.id}>
                    <div className='ssc-table-area draggable position-relative'>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId='Table'>
                                {(provided) => (
                                    <table
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className='w-100'
                                    >
                                        <thead>
                                            <tr>
                                                <th>
                                                    {isEmpty(orderingList)
                                                        ? 'Current Order'
                                                        : 'Updated Order'}
                                                </th>
                                                <th>Hiring Clients</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hiringClients?.length ? (
                                                hiringClients?.map((item, index) => (
                                                    <Draggable
                                                        key={item.id}
                                                        draggableId={item.id}
                                                        index={index}
                                                    >
                                                        {(provided) => (
                                                            <tr
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className='border-bottom border-bottom-0'
                                                            >
                                                                <td>{item?.hc_order}</td>
                                                                <td>{item?.name}</td>
                                                            </tr>
                                                        )}
                                                    </Draggable>
                                                ))
                                            ) : (
                                                <tr className='text-special text-center ms-5'>
                                                    <td>{EDIT_CUSTOMER.BUTTON_TEXT.NO_HC}</td>
                                                </tr>
                                            )}
                                            {provided.placeholder}
                                        </tbody>
                                    </table>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {reorderHcMutation.isPending && <CircleSpinner absolute size={'3rem'} />}
                    </div>
                </div>
                <div className='d-flex align-items-center justify-content-end gap-3'>
                    <button
                        type='button'
                        className={`form-white-button d-flex align-items-center justify-content-center border-0 cursor-pointer`}
                        onClick={() => {
                            setOrderingList({});
                            setHiringClients(tpa?.hiringClients);
                            setInitialHiringClients(transformData(accountData?.platformsWithHC));
                        }}
                        disabled={reorderHcMutation.isPending}
                    >
                        {EDIT_CUSTOMER.BUTTON_TEXT.CANCEL}
                    </button>
                    <button
                        type='submit'
                        className='ssc-primary-green-btn button-green-filled form-button d-flex align-items-center justify-content-center border-0'
                        onClick={() => setOpenConfirmationBox(true)}
                        disabled={isEmpty(orderingList) || reorderHcMutation.isPending}
                    >
                        {reorderHcMutation.isPending && (
                            <span className='me-1'>
                                <CircleSpinner />
                            </span>
                        )}
                        {BUTTONS.SAVE_BUTTON}
                    </button>
                </div>
            </SectionBox>
            <ConfirmationBox
                setConfirm={(v) => onConfirmHandler(v)}
                openConfirmationBox={openConfirmationBox}
                setOpenConfirmationBox={setOpenConfirmationBox}
                customMessage='Do you want to save changes?'
            />
        </div>
    );
};
